import * as React from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export interface ComboboxOption {
  value: string;
  label: string;
  searchableText?: string; // Additional text to search through (optional)
}

export interface ComboboxSelectProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  popoverClassName?: string;
  searchClassName?: string;
  triggerClassName?: string;
  renderOption?: (option: ComboboxOption) => React.ReactNode;
  renderValue?: (option: ComboboxOption | undefined) => React.ReactNode;
  filterFn?: (option: ComboboxOption, searchQuery: string) => boolean;
  triggerRef?: React.RefObject<HTMLButtonElement | null>;
  /** Keep the caller's option order instead of sorting alphabetically. */
  preserveOrder?: boolean;
}

/**
 * ComboboxSelect - A searchable select component with full keyboard navigation
 * 
 * Features:
 * - Type to search/filter options
 * - Arrow keys (↑/↓) to navigate
 * - Enter to select
 * - Escape to close
 * - Mouse hover support
 * - Auto-scroll highlighted items into view
 * 
 * @example
 * ```tsx
 * <ComboboxSelect
 *   options={[
 *     { value: "1", label: "Option 1" },
 *     { value: "2", label: "Option 2" }
 *   ]}
 *   value={selectedValue}
 *   onValueChange={setSelectedValue}
 *   placeholder="Select an option"
 * />
 * ```
 */
export function ComboboxSelect({
  options,
  value,
  onValueChange,
  placeholder = "Select an option...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found.",
  disabled = false,
  className,
  popoverClassName,
  searchClassName,
  triggerClassName,
  renderOption,
  renderValue,
  filterFn,
  triggerRef: externalTriggerRef,
  preserveOrder = false,
}: ComboboxSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const searchInputRef = React.useRef<HTMLInputElement>(null);
  const highlightedItemRef = React.useRef<HTMLDivElement>(null);
  const internalTriggerRef = React.useRef<HTMLButtonElement>(null);
  const triggerRef = externalTriggerRef || internalTriggerRef;

  // Scroll highlighted item into view
  React.useEffect(() => {
    if (highlightedIndex >= 0 && highlightedItemRef.current) {
      highlightedItemRef.current.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [highlightedIndex]);

  // Default filter function
  const defaultFilterFn = (option: ComboboxOption, query: string) => {
    const searchTerm = query.toLowerCase();
    const labelMatch = option.label.toLowerCase().includes(searchTerm);
    const valueMatch = option.value.toLowerCase().includes(searchTerm);
    const searchableTextMatch = option.searchableText?.toLowerCase().includes(searchTerm) || false;
    return labelMatch || valueMatch || searchableTextMatch;
  };

  const getFilteredOptions = () => {
    const filter = filterFn || defaultFilterFn;
    const filtered = options.filter((option) => filter(option, searchQuery));
    return preserveOrder
      ? filtered
      : filtered.sort((a, b) => a.label.localeCompare(b.label));
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (newOpen) {
      setHighlightedIndex(-1);
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 10);
    } else {
      setHighlightedIndex(-1);
    }
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    // Open on any alphanumeric key and start typing
    if (!open && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      setSearchQuery(e.key);
      setOpen(true);
    } else if (e.key === "Enter" && !open) {
      e.preventDefault();
      setSearchQuery("");
      setOpen(true);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
      setSearchQuery("");
    } else if (e.key === "Backspace" && !open) {
      e.preventDefault();
      setSearchQuery("");
      setOpen(true);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const filteredOptions = getFilteredOptions();

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) =>
        prev < filteredOptions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : 0));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      const selectedOption = filteredOptions[highlightedIndex];
      if (selectedOption) {
        onValueChange?.(selectedOption.value);
        setOpen(false);
        setHighlightedIndex(-1);
      }
    } else if (e.key === "Escape" || e.key === "Tab") {
      e.preventDefault();
      setOpen(false);
      setHighlightedIndex(-1);
    }
  };

  const handleSelect = (optionValue: string) => {
    onValueChange?.(optionValue);
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const selectedOption = options.find((opt) => opt.value === value);
  const filteredOptions = getFilteredOptions();

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between h-9 font-normal bg-transparent",
            !value && "text-muted-foreground",
            triggerClassName
          )}
          disabled={disabled}
          onKeyDown={handleTriggerKeyDown}
        >
          <span className="truncate">
            {renderValue
              ? renderValue(selectedOption)
              : selectedOption
              ? selectedOption.label
              : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          // Grow to fit long option labels; never narrower than the trigger.
          "w-auto min-w-[var(--radix-popover-trigger-width)] max-w-md p-0",
          popoverClassName,
        )}
        align="start"
      >
        <div className={cn("flex flex-col", className)}>
          <div className="p-2 border-b">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                ref={searchInputRef}
                placeholder={searchPlaceholder}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={handleKeyDown}
                className={cn("pl-8", searchClassName)}
              />
            </div>
          </div>
          {/* Plain scroll container: Radix ScrollArea lets long rows overflow
              horizontally instead of shrinking them to the popover width. */}
          <div className="max-h-72 overflow-y-auto overflow-x-hidden">
            <div className="p-1">
              {filteredOptions.map((option, index) => (
                <div
                  key={option.value}
                  ref={highlightedIndex === index ? highlightedItemRef : null}
                  className={cn(
                    "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-2 text-sm outline-hidden transition-colors",
                    highlightedIndex === index && "bg-accent text-accent-foreground",
                    value === option.value && "bg-accent/50",
                    "hover:bg-accent hover:text-accent-foreground"
                  )}
                  onClick={() => handleSelect(option.value)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      value === option.value ? "opacity-100" : "opacity-0"
                    )}
                  />
                  <span className="min-w-0 truncate">
                    {renderOption ? renderOption(option) : option.label}
                  </span>
                </div>
              ))}
              {filteredOptions.length === 0 && (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  {emptyMessage}
                </div>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Hook to easily convert data to ComboboxOption format
 * 
 * @example
 * ```tsx
 * const options = useComboboxOptions(
 *   matters,
 *   (m) => m.id.toString(),
 *   (m) => m.group_name ? `${m.group_name} - ${m.name}` : m.name,
 *   (m) => `${m.name} ${m.group_name || ''} ${getClientName(m)}`
 * );
 * ```
 */
export function useComboboxOptions<T>(
  data: T[],
  getValue: (item: T) => string,
  getLabel: (item: T) => string,
  getSearchableText?: (item: T) => string
): ComboboxOption[] {
  return React.useMemo(
    () =>
      data.map((item) => ({
        value: getValue(item),
        label: getLabel(item),
        searchableText: getSearchableText?.(item),
      })),
    [data, getValue, getLabel, getSearchableText]
  );
}
